import React, { useEffect, useRef, useState } from 'react'
import 'leaflet/dist/leaflet.css'

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'

import TimelineElement from './TimelineElement'
import TimelineDotComponent from './TimelineDotComponent'
import {
  Event,
  EventLocation,
  EventLocationResponse,
  EventMedia,
  EventMember,
  EventMemberResponse,
  EventPriceCategory,
  EventPriceCategoryResponse,
  EventResponse,
  MemberResponse,
  PermissionType,
  UserPermissions,
} from '../../../shared/types'
import SingleEvent from '../../../screens/App/Events/SingleEvent'
import { useSelector } from 'react-redux'
import {
  getEventLocations,
  getEventMember,
  getEventPriceCategories,
  getEvents,
  getMembers,
} from '../../../store/data/data.selectors'
import getEventObject from '../../../dataMapping/getEventObject'
import { getSearchInput, getUserPermissions } from '../../../store/settings/settings.selectors'
import TimelineElementEditor from '../../../screens/App/Admin/TimelineElementEditor'
import TextInputComponent from '../../../shared/components/TextInputComponent'
import ButtonComponent from '../../../shared/components/ButtonComponent'
import EventMap from './EventMap'
import '../../../shared/assets/fonts/font.css'
import UndoIcon from '@mui/icons-material/Undo'
import EditIcon from '@mui/icons-material/Edit'

export interface EventObject {
  event: Event
  eventPrices: EventPriceCategory[]
  media: EventMedia[]
  eventLocation?: EventLocation
  eventMember: EventMember[]
}

const EventComponent: React.FC = () => {
  const headerHeight = 300
  const theme = useTheme()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downLg = useMediaQuery(theme.breakpoints.down('lg'))

  const userPermissions: UserPermissions = useSelector(getUserPermissions)
  const events: EventResponse | undefined = useSelector(getEvents)
  const members: MemberResponse | undefined = useSelector(getMembers)
  const eventMember: EventMemberResponse | undefined = useSelector(getEventMember)
  const eventPrices: EventPriceCategoryResponse | undefined = useSelector(getEventPriceCategories)
  const eventLocations: EventLocationResponse | undefined = useSelector(getEventLocations)
  const searchInput: string | undefined = useSelector(getSearchInput)

  const [data, setData] = useState<EventObject[]>([])
  const [adminMode, setAdminMode] = useState<boolean>(false)
  const [activeEvent, setActiveEvent] = useState<EventObject | undefined>()

  const [showNewEventDialog, setShowNewEventDialog] = useState<boolean>(false)
  const [newEventYear, setNewEventYear] = useState<number | undefined>()

  useEffect(() => {
    const tempEventId = activeEvent?.event.id
    setActiveEvent(data.find(elem => elem.event.id === tempEventId))
  }, [data])

  useEffect(() => {
    if (activeEvent?.event.id !== 'new') {
      setAdminMode(false)
    }
  }, [activeEvent?.event.id])

  useEffect(() => {
    events && eventMember && eventPrices && eventLocations && setData(getEventObject(events, eventMember, eventPrices, eventLocations, members, searchInput))
  }, [events, eventMember, eventPrices, eventLocations, searchInput])

  const ref = useRef(null)
  const [contentHeight, setContentHeight] = useState<number>(0)

  const useStyles = makeStyles((theme: Theme) => ({
    root: {
      width: '100%',
      minHeight: 'calc(100vh - 250px)',
      display: 'flex',
      position: 'relative',
      justifyContent: downMd ? 'center' : 'space-between',
    },
    timeline: {
      flexGrow: '0!important',
      width: downMd ? '360px' : '450px',
      overflowY: 'scroll',
      overflowX: 'hidden',
    },
    item: {
      '&:before': {
        flex: 1,
        content: '',
        padding: '6px 0px!important',
      },
    },
    settingsIcon: {
      position: 'absolute',
      top: '-10px',
      right: '10px',
      color: theme.palette.primary.main,
    },
    icons: {
      transform: 'scale(1.2)',
    },
    addIcon: {
      borderRadius: 42,
      height: '52px',
      width: '52px',
      border: `2px solid ${theme.palette.primary.main}`,
      position: 'absolute',
      top: downSm ? '10px' : (downMd ? '44px' : '44px'),
      left: downSm ? '46px' : (downMd ? '80px' : (downLg ? '174px' : '114px')),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '24px',
      color: theme.palette.primary.main,
    },
    image: {
      width: '100%',
      height: 'auto',
      cursor: 'pointer',
    },
  }))

  const classes = useStyles()

  const createNewEvent = () => {
    setActiveEvent({
      event: {
        id: 'new',
        title: `Veranstaltung ${newEventYear}`,
        description: ' ',
        organizer: [],
        eventFrom: {
          year: newEventYear,
        },
      },
      eventPrices: [],
      eventMember: [],
      media: [],
    })
  }

  useEffect(() => ref?.current && setContentHeight(ref?.current.offsetHeight - headerHeight - 20))

  return (
    <Box sx={{ position: 'relative' }}>
      <Box className={classes.root}>
        {userPermissions.events.find(elem => elem === PermissionType.create) && (!downMd || (downMd && !activeEvent)) &&
          <IconButton
            onClick={() => setShowNewEventDialog(true)}
          >
            <Box component='div' className={classes.addIcon}>
              <span style={{ marginBottom: '4px' }}>
                +
              </span>
            </Box>
          </IconButton>}
        {(!downMd || (downMd && !activeEvent)) &&
          <Paper sx={{ margin: downSm ? '0px' : '24px', padding: downSm ? '8px' : '24px' }}>
            <Timeline
              position='right' className={classes.timeline}
              sx={{ marginTop: '40px' }}
            >
              {data.map((element: EventObject, index: number) => {
                const color = 'primary'
                const filled = element.event.id === activeEvent?.event.id ? 'filled' : 'outlined'

                return (
                  <TimelineItem
                    sx={{ minHeight: '220px' }} key={element.event.eventFrom?.year}
                    className={classes.item}
                  >
                    <TimelineSeparator>
                      <TimelineDot
                        onClick={() => {
                          setActiveEvent(element)
                        }}
                        color={color}
                        variant={filled}
                      >
                        <TimelineDotComponent
                          year={element.event.eventFrom?.year}
                          active={element.event.id === activeEvent?.event.id}
                        />
                      </TimelineDot>
                      {(index < data.length - 1) && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent
                      sx={{ position: 'relative' }}
                      onClick={() => {
                        setActiveEvent(element)
                      }}
                    >
                      <TimelineElement
                        active={element.event.id === activeEvent?.event.id}
                        element={element}
                        width={downLg ? (downMd ? (downSm ? 'medium' : 'big') : 'medium') : 'big'}
                      />
                      {userPermissions.events.find(elem => elem === PermissionType.update) &&
                        <EditIcon
                          sx={{
                            color: theme.palette.primary.main,
                            position: 'absolute',
                            top: '10px',
                            right: '30px',
                            zIndex: 100,
                            cursor: 'pointer',
                          }}
                          onClick={() => setAdminMode(!adminMode)}
                        />}
                    </TimelineContent>
                  </TimelineItem>
                )
              },
              )}
            </Timeline>
          </Paper>}
        {downMd
          ? (
            <>
              {
                activeEvent &&
                  <div style={{ width: '100%', overflow: 'hidden' }} ref={ref}>
                    <Box
                      sx={{ margin: '8px', cursor: 'pointer' }}
                      onClick={() => {
                        setActiveEvent(undefined)
                      }}
                    ><UndoIcon />
                    </Box>
                    <Paper sx={{ padding: '8px' }}>
                      {adminMode
                        ? <TimelineElementEditor data={activeEvent} setAdminMode={(mode: boolean) => setAdminMode(mode)} />
                        : <SingleEvent data={activeEvent} setAdminMode={setAdminMode} />}
                    </Paper>
                    <Box
                      sx={{ textAlign: 'right', margin: '8px', cursor: 'pointer' }}
                      onClick={() => {
                        setActiveEvent(undefined)
                      }}
                    ><UndoIcon />
                    </Box>
                  </div>
              }
            </>)
          : (
            <div style={{ width: 'calc(100% - 340px)', marginRight: '20px' }}>
              {
                activeEvent
                  ? (
                    <div ref={ref}>
                      {adminMode
                        ? <TimelineElementEditor data={activeEvent} setAdminMode={(mode: boolean) => setAdminMode(mode)} />
                        : <SingleEvent data={activeEvent} setAdminMode={() => console.log('TODO')} />}
                    </div>)
                  : (
                    <Box sx={{ height: window.screen.availHeight - headerHeight - 20, marginTop: '24px' }}>
                      <EventMap
                        height={window.screen.availHeight - headerHeight - 20} mapZoom={6}
                        positions={[]}
                      />
                    </Box>)
              }
            </div>)}
      </Box>
      <Dialog
        open={showNewEventDialog} onClose={() => setShowNewEventDialog(false)}
        sx={{ '& .MuiDialog-paper': { borderRadius: 0, minWidth: '400px', maxWidth: '400px' } }}
      >
        <Stack sx={{ p: 3 }}>
          <Stack
            direction='row' spacing={3} alignItems='center'
            justifyContent='space-between'
          >
            <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
              Neue Veranstaltung
            </DialogTitle>
          </Stack>
          <DialogContent sx={{ p: 0, overflow: 'visible' }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextInputComponent
                  label='Veranstaltungsjahr'
                  required
                  fullWidth
                  onChange={(value?: string | number) => {
                    const newValue: number = parseInt(value as string, 10)
                    setNewEventYear(newValue)
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
        </Stack>
        <DialogActions sx={{ p: 3 }}>
          <ButtonComponent
            color='primary'
            variant='text'
            text='Verwerfen'
            onClick={() => {
              setNewEventYear(undefined)
              setShowNewEventDialog(false)
            }}
          />
          <ButtonComponent
            color='primary'
            variant='contained'
            text='Erstellen'
            onClick={() => {
              createNewEvent()
              setAdminMode(true)
              setShowNewEventDialog(false)
            }}
          />
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EventComponent
